
import type { PropType } from 'vue'
import type { FormCapacity, FormWeekLimit } from '@/modules/TimeSlotsModule/types/time-slot-form.types'

import { defineComponent } from 'vue'

// @components
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// @module components
import TimeSlotBadge from '@/modules/TimeSlotsModule/components/TimeSlotBadge/index.vue'
import TimeSlotTimeRange from '@/modules/TimeSlotsModule/components/TimeSlotTimeRange/index.vue'
import TimeSlotSectorCheckbox from '@/modules/TimeSlotsModule/components/TimeSlotSectorCheckbox/index.vue'

// @types
import type { TimeSlotZone } from '@/new-services/types/time-slot-zones'

export default defineComponent({
    components: {
        Checkbox,
        TheInput,
        Loader,
        TheButton,
        TimeSlotBadge,
        TimeSlotTimeRange,
        TimeSlotSectorCheckbox
    },
    props: {
        isMain: {
            type: Boolean,
            default: false
        },
        index: {
            type: Number,
            default: 0
        },
        isActive: {
            type: Boolean,
            default: false
        },
        sectorsLoading: {
            type: Boolean,
            default: false,
        },
        sectorsFailed: {
            type: Boolean,
            default: false,
        },
        sectors: {
            type: Array as PropType<Array<TimeSlotZone>>,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        timeFrom: {
            type: [Date, null] as PropType<Date | null>,
            default: null
        },
        timeTo: {
            type: [Date, null] as PropType<Date | null>,
            default: null
        },
        capacities: {
            type: Array as PropType<Array<FormCapacity>>,
            default: () => []
        },
        sectorIds: {
            type: Array as PropType<Array<Number>>,
            default: () => []
        },
        resourceLimits: {
            type: Array as PropType<Array<FormWeekLimit>>,
            default: () => []
        },
        mainTimeRange: {
            type: Array as PropType<Array<Date | null>>,
            default: () => []
        },
        reservedTimeRange: {
            type: Array as PropType<Array<Array<Date>>>,
            default: () => []
        }
    },
    emits: [
        'toggle-active',
        'reload-sectors',
        'time-from',
        'time-to',
        'change-time-slot',
        'update-capacity',
        'select-sector',
        'unselect-sector',
        'delete-slot'
    ],
    setup(props, { emit }) {
        const onToggleActive = () => {
            if (props.disabled) return
            emit('toggle-active')
        }

        const onReloadSectors = () => {
            emit('reload-sectors')
        }

        const onTimeFrom = (timeFrom: Date | null) => emit('time-from', timeFrom)
        const onTimeTo = (timeTo: Date | null) => emit('time-to', timeTo)
        const onTimeSlotChange = (payload: { timeFrom: Date | null; timeTo: Date | null }) => emit('change-time-slot', payload)

        const updateCapacity = (capacity: { weekday: number; amount: string | null; }) => {
            if (props.disabled) return
            emit('update-capacity', { weekday: capacity.weekday, amount: capacity.amount })
        }

        const isSectorSelected = (sector: TimeSlotZone) => props.sectorIds.includes(sector.id)

        const onSectorToggle = (sector: TimeSlotZone) => {
            if (props.disabled) return

            if (isSectorSelected(sector)) {
                emit('unselect-sector', sector.id)  
            } else {
                emit('select-sector', sector.id)
            }
        }

        const onDeleteSlot = () => {
            if (props.disabled || props.isMain) return
            emit('delete-slot')
        }

        return {
            onToggleActive,
            onReloadSectors,
            onTimeFrom,
            onTimeTo,
            onTimeSlotChange,
            updateCapacity,
            isSectorSelected,
            onSectorToggle,
            onDeleteSlot
        }
    }
})
